/**
 * PipelinePolicySections — secciones Routing · Optimización · Loops/Avanzado
 * de la política LLM del pipeline (Sprint 278 — ROUTE-PIPE-1/A, REQ-LLM-006 §8.1),
 * más Ahorro de tokens · Caché · Optimizadores · Ejecución · Presupuesto
 * (Sprint 280 — CHAT-TOK-1/B, REQ-LLM-006 rev. 16-Oct-2026, ADR-120).
 *
 * Dos usos:
 *  - `PlatformDrLlmPolicyPage` / `TenantLlmPolicyPage` (modo `dr`/`tenant`):
 *    campos planos de la API de política (tenant con InheritOrValue y techos
 *    visibles en los numéricos).
 *  - Editores de hive/agente (`PipelinePolicyOverrides`): editan los bloques
 *    anidados `settings.{routing,optimization,cache,execution,loop,autonomy,timeouts}`
 *    con la misma semántica de herencia (null = retirar el override).
 *
 * `scope` (`dr` | `tenant` | `entity`) aplica la columna «Capas» del REQ:
 * los campos que la capa no puede fijar se ocultan (o se muestran como solo
 * lectura cuando se heredan del DR). Los 11 campos con consumidor posterior
 * (ADR-120 D9) llevan una nota de disponibilidad de producto (i18n ES/EN).
 */

import type { ReactNode } from 'react';
import {
  Coins,
  Database,
  GitBranch,
  Play,
  Repeat,
  SlidersHorizontal,
  Sparkles,
  Wallet,
} from 'lucide-react';
import { Collapsible } from '@vanguard-atlas/shared/src/components/ui';
import { useI18n } from '@/i18n';
import {
  policyFieldsToSettings,
  settingsToPolicyFields,
  type SettingsDict,
} from '../lib/policySettings';
import { Input } from '@/components/ui/input';
import type { PipelinePolicyFields, PolicyLayer } from '../types/policy';
import {
  ASYNC_SERVICE_TIER_LABELS,
  ASYNC_TIER_UNAVAILABLE_LABELS,
  BUDGET_PROFILE_LABELS,
  CACHE_PARTITION_LABELS,
  CACHE_TTL_LABELS,
  DASHBOARD_WINDOW_LABELS,
  FRONTIER_UNAVAILABLE_LABELS,
  GROUNDING_LABELS,
  OUTPUT_EFFORT_LABELS,
  PLANNING_FAILURE_LABELS,
  PLAN_REVIEW_LABELS,
  ROUTING_FAILURE_LABELS,
  ROUTING_MODE_LABELS,
  TOOL_EXPOSURE_LABELS,
  translateLabels,
} from '../lib/policyLabels';
import {
  ALL_PIPELINE_DEFAULTS,
  ASYNC_SERVICE_TIERS,
  ASYNC_TIER_ON_UNAVAILABLE_VALUES,
  BUDGET_AWARE_PROFILES,
  DASHBOARD_WINDOWS,
  FRONTIER_ON_UNAVAILABLE_VALUES,
  GROUNDING_CHECKS,
  OUTPUT_EFFORTS,
  PHASE_OF_FIELD,
  PIPELINE_CAPS,
  PLAN_REVIEWS,
  PLANNING_ON_FAILURES,
  PROMPT_CACHE_PARTITIONS,
  PROMPT_CACHE_TTLS,
  ROUTING_MODES,
  ROUTING_ON_FAILURES,
  TOKEN_ECONOMICS_LAYERS,
  TOOL_EXPOSURES,
} from '../types/policy';

/** Alcance de la superficie: DR, tenant o overrides de hive/agente. */
export type PolicyScope = 'dr' | 'tenant' | 'entity';

/** ¿Puede `scope` fijar `field`? (columna «Capas» de REQ-LLM-006 rev.) */
export function isFieldSettable(
  field: string,
  scope: PolicyScope
): boolean {
  // Frontier concurrency is a tenant-only quota (plan/tenant policy); it is
  // not settable at DR or hive/agent scope.
  if (field === 'frontier_max_concurrent_runs') return scope === 'tenant';
  const layers = (TOKEN_ECONOMICS_LAYERS as Record<string, PolicyLayer[]>)[
    field
  ];
  if (!layers) return true; // 19 campos Sprint 278: sin restricción.
  const layer: PolicyLayer =
    scope === 'dr' ? 'dr' : scope === 'tenant' ? 'tenant' : 'hive';
  return layers.includes(layer);
}

/** Indicación de fase del campo (F2/F3) o null si aplica ya (F1.x). */
export function phaseOfField(
  field: string
): 'F2' | 'F3' | null {
  return (
    (PHASE_OF_FIELD as Record<string, 'F2' | 'F3'>)[field] ?? null
  );
}

/** Nota de disponibilidad del campo (ejecución asíncrona / presupuesto). */
export function PhaseBadge({ phase }: { phase: 'F2' | 'F3' }) {
  const { t } = useI18n();
  const label =
    phase === 'F2'
      ? t('policy.appliesAsyncExecution')
      : t('policy.availableForBudget');
  return (
    <span
      data-testid={`phase-badge-${phase}`}
      title={label}
      className="ml-2 inline-flex items-center rounded-full border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-300"
    >
      {label}
    </span>
  );
}

/** Etiqueta con insignia de fase cuando el campo es F2/F3. */
function FieldLabel({ field, label }: { field: string; label: string }) {
  const phase = phaseOfField(field);
  return (
    <span>
      {label}
      {phase && <PhaseBadge phase={phase} />}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Inherit-or-value selector (compartido; null = heredar/retirar el override)
// ---------------------------------------------------------------------------

export function InheritOrValue<T extends string | boolean | number>({
  label,
  labelNode,
  value,
  onChange,
  defaultValue,
  children,
}: {
  label: string;
  /** Nodo visible (etiqueta + insignia de fase); por defecto `label`. */
  labelNode?: ReactNode;
  value: T | null | undefined;
  onChange: (v: T | null) => void;
  /** Valor propuesto al desactivar «Inherit». */
  defaultValue?: T;
  children: (setValue: (v: T) => void) => ReactNode;
}) {
  const { t } = useI18n();
  const inherits = value === null || value === undefined;
  const fallback =
    defaultValue ??
    (typeof value === 'boolean' ? true : typeof value === 'number' ? 1 : '');
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium flex-1">
          {labelNode ?? label}
        </span>
        <label className="flex items-center gap-1 text-xs text-gray-500">
          <input
            type="checkbox"
            aria-label={t('policy.inheritField', { field: label })}
            checked={inherits}
            onChange={(e) =>
              onChange(e.target.checked ? null : (fallback as T))
            }
          />
          {t('policy.inherit')}
        </label>
      </div>
      {!inherits && <div className="pl-2">{children((v) => onChange(v))}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field primitives
// ---------------------------------------------------------------------------

function CapHint({ cap }: { cap: number }) {
  const { t } = useI18n();
  return (
    <span className="text-xs text-gray-400">{t('policy.cap', { cap })}</span>
  );
}

function NumberField({
  value,
  onChange,
  min = 1,
  max,
  step,
  label,
  children,
  disabled,
}: {
  value: number | null | undefined;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  children?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {children}
      <Input
        type="number"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        className="w-32"
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {max !== undefined && <CapHint cap={max} />}
    </div>
  );
}

function SelectField<T extends string>({
  value,
  options,
  labels,
  onChange,
  label,
  disabled,
}: {
  value: T | null | undefined;
  options: T[];
  /** Etiquetas localizadas por valor técnico (fallback: `_` → espacio). */
  labels?: Record<string, string>;
  onChange: (v: T) => void;
  label?: string;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  return (
    <select
      aria-label={label}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as T)}
      className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm disabled:opacity-60"
    >
      <option value="">{t('policy.selectPlaceholder')}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {labels?.[option] ?? option.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}

function ToggleField({
  value,
  onChange,
  label,
  labelNode,
  hint,
  disabled,
}: {
  value: boolean | null | undefined;
  onChange: (v: boolean) => void;
  label: string;
  /** Nodo visible (etiqueta + insignia de fase); por defecto `label`. */
  labelNode?: ReactNode;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        aria-label={label}
        checked={!!value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {labelNode ?? label}
        {hint && (
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

export interface ResponsibleLock {
  /** Campos del perfil frontera gobernados por el responsable (REQ-AGT-009). */
  fields: (keyof PipelinePolicyFields)[];
  /** Motivo mostrado junto al campo en solo lectura. */
  hint: string;
}

export interface PipelinePolicySectionsProps {
  /** `tenant`/`overrides` muestran el tri-estado heredar/valor por campo. */
  mode: 'dr' | 'inherit';
  value: PipelinePolicyFields;
  onChange: (patch: Partial<PipelinePolicyFields>) => void;
  /**
   * Alcance de la superficie (columna «Capas»): `dr` (página del DR),
   * `tenant` (página del tenant) o `entity` (editores de hive/agente).
   * Por defecto se deriva de `mode` (`dr` → `dr`, si no → `tenant`).
   */
  scope?: PolicyScope;
  /** Campos en solo lectura por gobierno del responsable frontera. */
  responsibleLock?: ResponsibleLock;
}

export function PipelinePolicySections({
  mode,
  value,
  onChange,
  scope,
  responsibleLock,
}: PipelinePolicySectionsProps) {
  const inheriting = mode === 'inherit';
  const effectiveScope: PolicyScope = scope ?? (mode === 'dr' ? 'dr' : 'tenant');
  const set = (patch: Partial<PipelinePolicyFields>) => onChange(patch);
  const { t } = useI18n();
  const drOnlyNote = t('policy.drOnly');
  const inheritedNote = t('policy.inheritedFromDr');
  const readOnlyNote = `${drOnlyNote} — ${t('policy.readOnlyHere')}`;
  /** Traduce un mapa de etiquetas de enum (valor técnico → texto localizado). */
  const L = (labels: Record<string, string>) => translateLabels(t, labels);

  const responsibleLocked = (field: keyof PipelinePolicyFields): boolean =>
    responsibleLock?.fields.includes(field) ?? false;

  /** null cuando el campo no existe en este alcance (columna «Capas»).
   * En alcance tenant se muestra todo (lo no fijable, como solo lectura);
   * en DR y en hive/agente se oculta lo no fijable. */
  const gated = (field: keyof PipelinePolicyFields): boolean =>
    effectiveScope !== 'tenant' &&
    !isFieldSettable(field as string, effectiveScope);

  /** Solo lectura: visible pero no fijable aquí (hereda del DR). */
  const locked = (field: keyof PipelinePolicyFields): boolean =>
    effectiveScope === 'tenant' &&
    (TOKEN_ECONOMICS_LAYERS as Record<string, PolicyLayer[]>)[
      field as string
    ] !== undefined &&
    !isFieldSettable(field as string, effectiveScope);

  const lockHint = (field: keyof PipelinePolicyFields): string | undefined => {
    if (!locked(field)) return undefined;
    return field === 'estimator_id' ? readOnlyNote : inheritedNote;
  };

  /** Fila del estimador: valor único en v1 → solo lectura en todo alcance. */
  const estimatorRow = () => {
    const field = 'estimator_id' as keyof PipelinePolicyFields;
    if (gated(field)) return null;
    const current =
      (value[field] as string | null | undefined) ??
      (ALL_PIPELINE_DEFAULTS.estimator_id as string);
    const note = effectiveScope === 'dr' ? readOnlyNote : inheritedNote;
    return (
      <div key={field}>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-64">
            <FieldLabel field={field} label={t('policy.estimator')} />
          </span>
          <Input
            type="text"
            aria-label={t('policy.estimator')}
            className="w-48"
            value={current}
            disabled
            readOnly
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{note}</p>
      </div>
    );
  };

  const numberField = (
    field: keyof PipelinePolicyFields,
    label: string,
    opts: { min?: number; max?: number; step?: number; hint?: string } = {}
  ) => {
    if (gated(field)) return null;
    const readOnly = locked(field) || responsibleLocked(field);
    const labelNode = <FieldLabel field={field as string} label={label} />;
    const hint = responsibleLocked(field)
      ? responsibleLock?.hint
      : opts.hint ?? lockHint(field);
    const input = (v: number | null | undefined, change: (n: number) => void) => (
      <NumberField
        label={label}
        value={v}
        onChange={change}
        min={opts.min}
        max={opts.max}
        step={opts.step}
        disabled={readOnly}
      >
        <span className="w-64">{labelNode}</span>
      </NumberField>
    );
    if (!inheriting || readOnly) {
      const raw = value[field] as number | null | undefined;
      return (
        <div key={field}>
          {input(raw, (n) => set({ [field]: n }))}
          {hint && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {hint}
            </p>
          )}
        </div>
      );
    }
    return (
      <InheritOrValue
        key={field}
        label={label}
        labelNode={labelNode}
        value={value[field] as number | null | undefined}
        defaultValue={
          (ALL_PIPELINE_DEFAULTS[field as string] as number) ?? opts.min ?? 1
        }
        onChange={(v) => set({ [field]: v })}
      >
        {(change) => input(value[field] as number | null | undefined, change)}
      </InheritOrValue>
    );
  };

  const selectField = <T extends string,>(
    field: keyof PipelinePolicyFields,
    label: string,
    options: T[],
    labels?: Record<string, string>
  ) => {
    if (gated(field)) return null;
    const readOnly = locked(field) || responsibleLocked(field);
    const hint = responsibleLocked(field) ? responsibleLock?.hint : lockHint(field);
    const labelNode = <FieldLabel field={field as string} label={label} />;
    const select = (v: T | null | undefined, change: (s: T) => void) => (
      <div className="flex items-center gap-2 text-sm">
        <span className="w-64">
          {labelNode}
        </span>
        <SelectField
          label={label}
          value={v}
          options={options}
          labels={labels}
          onChange={change}
          disabled={readOnly}
        />
      </div>
    );
    if (!inheriting || readOnly) {
      return (
        <div key={field}>
          {select(value[field] as T, (s) => set({ [field]: s }))}
          {hint && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {hint}
            </p>
          )}
        </div>
      );
    }
    return (
      <InheritOrValue
        key={field}
        label={label}
        labelNode={labelNode}
        value={value[field] as T | null | undefined}
        defaultValue={ALL_PIPELINE_DEFAULTS[field as string] as T}
        onChange={(v) => set({ [field]: v })}
      >
        {(change) => select(value[field] as T | null | undefined, change)}
      </InheritOrValue>
    );
  };

  const toggleField = (
    field: keyof PipelinePolicyFields,
    label: string,
    hint?: string
  ) => {
    if (gated(field)) return null;
    const readOnly = locked(field) || responsibleLocked(field);
    const labelNode = <FieldLabel field={field as string} label={label} />;
    const toggle = (v: boolean | null | undefined, change: (b: boolean) => void) => (
      <ToggleField
        value={v}
        onChange={change}
        label={label}
        labelNode={labelNode}
        hint={responsibleLocked(field) ? responsibleLock?.hint : hint ?? lockHint(field)}
        disabled={readOnly}
      />
    );
    if (!inheriting || readOnly) {
      return (
        <div key={field}>
          {toggle(value[field] as boolean, (b) => set({ [field]: b }))}
        </div>
      );
    }
    return (
      <InheritOrValue
        key={field}
        label={label}
        labelNode={labelNode}
        value={value[field] as boolean | null | undefined}
        defaultValue={
          ALL_PIPELINE_DEFAULTS[field as string] as boolean
        }
        onChange={(v) => set({ [field]: v })}
      >
        {(change) =>
          toggle(value[field] as boolean | null | undefined, change)
        }
      </InheritOrValue>
    );
  };

  return (
    <div className="space-y-6" data-testid="pipeline-policy-sections">
      {/* ── Routing ─────────────────────────────────────────────────── */}
      <Collapsible
        testId="pipeline-section-routing"
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
        trigger={
          <span className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span role="heading" aria-level={2} className="text-lg font-semibold">{t('policy.routingTitle')}</span>
          </span>
        }
        contentClassName="space-y-4 pt-4"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.routingHint')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {toggleField(
            'routing_enabled',
            t('policy.routingEnabled'),
            t('policy.routingEnabledHint')
          )}
          {selectField(
            'routing_mode',
            t('policy.routingMode'),
            ROUTING_MODES,
            L(ROUTING_MODE_LABELS)
          )}
          {selectField(
            'routing_on_failure',
            t('policy.routingOnFailure'),
            ROUTING_ON_FAILURES,
            L(ROUTING_FAILURE_LABELS)
          )}
          {numberField(
            'routing_confidence_threshold',
            t('policy.routingConfidence'),
            {
              min: 0,
              max: 1,
              step: 0.05,
              hint: t('policy.routingConfidenceHint'),
            }
          )}
          {numberField(
            'routing_timeout_ms',
            t('policy.routingTimeout'),
            {
              min: 1,
              max: PIPELINE_CAPS.routing_timeout_ms,
            }
          )}
          {numberField(
            'routing_budget_tokens',
            t('policy.routingBudget'),
            {
              min: 0,
              hint: t('policy.routingBudgetHint'),
            }
          )}
          {selectField(
            'planning_on_failure',
            t('policy.planningOnFailure'),
            PLANNING_ON_FAILURES,
            L(PLANNING_FAILURE_LABELS)
          )}
          {selectField(
            'grounding_check',
            t('policy.groundingCheck'),
            GROUNDING_CHECKS,
            L(GROUNDING_LABELS)
          )}
          {selectField(
            'plan_review',
            t('policy.planReview'),
            PLAN_REVIEWS,
            L(PLAN_REVIEW_LABELS)
          )}
          {toggleField(
            'retrieval_gating_enabled',
            t('policy.retrievalGating'),
            t('policy.retrievalGatingHint')
          )}
        </div>
      </Collapsible>

      {/* ── Optimización ────────────────────────────────────────────── */}
      <Collapsible
        testId="pipeline-section-optimization"
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
        trigger={
          <span className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span role="heading" aria-level={2} className="text-lg font-semibold">{t('policy.optimizationTitle')}</span>
          </span>
        }
        contentClassName="space-y-4 pt-4"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.optimizationHint')}
        </p>
        {toggleField(
          'optimization_enabled',
          t('policy.optimizationEnabled'),
          t('policy.optimizationEnabledHint')
        )}
      </Collapsible>

      {/* ── Loops/Avanzado ──────────────────────────────────────────── */}
      <Collapsible
        testId="pipeline-section-loops"
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
        trigger={
          <span className="flex items-center gap-2">
            <Repeat className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span role="heading" aria-level={2} className="text-lg font-semibold">{t('policy.loopsTitle')}</span>
          </span>
        }
        contentClassName="space-y-4 pt-4"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.loopsHint')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {numberField('max_loop_iterations', t('policy.maxLoopIterations'), {
            min: 1,
            max: PIPELINE_CAPS.max_loop_iterations,
          })}
          {toggleField(
            'frontier_agents_enabled',
            t('policy.frontierEnabled'),
            t('policy.frontierEnabledHint')
          )}
          {selectField(
            'frontier_on_unavailable',
            t('policy.frontierOnUnavailable'),
            FRONTIER_ON_UNAVAILABLE_VALUES,
            L(FRONTIER_UNAVAILABLE_LABELS)
          )}
          {numberField(
            'frontier_max_duration_minutes',
            t('policy.frontierMaxDuration'),
            { min: 1, max: PIPELINE_CAPS.frontier_max_duration_minutes }
          )}
          {numberField(
            'frontier_max_concurrent_runs',
            t('policy.frontierMaxConcurrentRuns'),
            {
              min: 0,
              max: PIPELINE_CAPS.frontier_max_concurrent_runs,
              hint: t('policy.frontierMaxConcurrentRunsHint'),
            }
          )}
          {numberField(
            'foreground_timeout_seconds',
            t('policy.foregroundTimeout'),
            {
              min: 1,
              max: PIPELINE_CAPS.foreground_timeout_seconds,
            }
          )}
          {numberField(
            'delegation_sync_timeout_seconds',
            t('policy.delegationSyncTimeout'),
            { min: 1, max: PIPELINE_CAPS.delegation_sync_timeout_seconds }
          )}
          {numberField(
            'delegation_background_timeout_seconds',
            t('policy.delegationBackgroundTimeout'),
            { min: 1, max: PIPELINE_CAPS.delegation_background_timeout_seconds }
          )}
          {numberField(
            'fanout_aggregation_timeout_seconds',
            t('policy.fanoutTimeout'),
            { min: 1, max: PIPELINE_CAPS.fanout_aggregation_timeout_seconds }
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('policy.loopsClamps')}
        </p>
      </Collapsible>

      {/* ── Ahorro de tokens (Sprint 280 — solo configuración; el panel lo hace H) */}
      <Collapsible
        testId="pipeline-section-token-savings"
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
        trigger={
          <span className="flex items-center gap-2">
            <Coins className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span role="heading" aria-level={2} className="text-lg font-semibold">{t('policy.tokenSavings')}</span>
          </span>
        }
        contentClassName="space-y-4 pt-4"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.tokenSavingsHint')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {toggleField(
            'measurement_enabled',
            t('policy.measurementEnabled'),
            t('policy.measurementEnabledHint')
          )}
          {estimatorRow()}
          {numberField(
            'compare_tolerance_pct',
            t('policy.compareTolerance'),
            {
              min: 0,
              max: PIPELINE_CAPS.compare_tolerance_pct,
              step: 0.5,
              hint: t('policy.compareToleranceHint'),
            }
          )}
          {toggleField(
            'estimator_calibration',
            t('policy.estimatorCalibration'),
            t('policy.estimatorCalibrationHint')
          )}
          {numberField(
            'count_tokens_sample_pct',
            t('policy.exactCountSample'),
            {
              min: 0,
              max: PIPELINE_CAPS.count_tokens_sample_pct,
              step: 0.1,
              hint: t('policy.exactCountSampleHint'),
            }
          )}
          {numberField(
            'ledger_retention_days',
            t('policy.ledgerRetention'),
            {
              min: 1,
              max: PIPELINE_CAPS.ledger_retention_days,
              hint: t('policy.ledgerRetentionHint'),
            }
          )}
          {selectField(
            'dashboard_window_default',
            t('policy.dashboardWindow'),
            DASHBOARD_WINDOWS,
            L(DASHBOARD_WINDOW_LABELS)
          )}
          {toggleField(
            'semantic_compression_allow',
            t('policy.semanticCompression'),
            t('policy.semanticCompressionHint')
          )}
          {numberField(
            'platform_quality_holdout_pct',
            t('policy.platformHoldout'),
            {
              min: 0,
              max: PIPELINE_CAPS.platform_quality_holdout_pct,
              step: 0.1,
              hint: t('policy.platformHoldoutHint'),
            }
          )}
          {numberField(
            'optimization_holdout_pct',
            t('policy.optimizationHoldout'),
            {
              min: 0,
              max: PIPELINE_CAPS.optimization_holdout_pct,
              step: 0.5,
              hint: t('policy.optimizationHoldoutHint'),
            }
          )}
        </div>
      </Collapsible>

      {/* ── Caché (Sprint 280) ─────────────────────────────────────── */}
      <Collapsible
        testId="pipeline-section-cache"
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
        trigger={
          <span className="flex items-center gap-2">
            <Database className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span role="heading" aria-level={2} className="text-lg font-semibold">{t('policy.cache')}</span>
          </span>
        }
        contentClassName="space-y-4 pt-4"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.cacheHint')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {toggleField(
            'prompt_cache_enabled',
            t('policy.promptCacheEnabled'),
            t('policy.promptCacheEnabledHint')
          )}
          {selectField(
            'prompt_cache_ttl',
            t('policy.promptCacheTtl'),
            PROMPT_CACHE_TTLS,
            L(CACHE_TTL_LABELS)
          )}
          {selectField(
            'prompt_cache_partition',
            t('policy.promptCachePartition'),
            PROMPT_CACHE_PARTITIONS,
            L(CACHE_PARTITION_LABELS)
          )}
          {toggleField(
            'prompt_cache_shared_platform_allowed',
            t('policy.sharedPlatformPrefix'),
            t('policy.sharedPlatformPrefixHint')
          )}
          {toggleField(
            'prompt_cache_extended_retention_allowed',
            t('policy.extendedRetention'),
            t('policy.extendedRetentionHint')
          )}
        </div>
      </Collapsible>

      {/* ── Optimizadores (Sprint 280; O4/O5 en F2) ─────────────────── */}
      <Collapsible
        testId="pipeline-section-optimizers"
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
        trigger={
          <span className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span role="heading" aria-level={2} className="text-lg font-semibold">{t('policy.optimizers')}</span>
          </span>
        }
        contentClassName="space-y-4 pt-4"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.optimizersHint')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {selectField(
            'tool_exposure',
            t('policy.toolExposure'),
            TOOL_EXPOSURES,
            L(TOOL_EXPOSURE_LABELS)
          )}
          {numberField(
            'tool_deferred_threshold_tokens',
            t('policy.deferredLoadThreshold'),
            {
              min: 1000,
              hint: t('policy.deferredLoadThresholdHint'),
            }
          )}
          {numberField(
            'compaction_trigger_pct',
            t('policy.compactionTrigger'),
            {
              min: 10,
              max: PIPELINE_CAPS.compaction_trigger_pct,
              hint: t('policy.compactionTriggerHint'),
            }
          )}
          {numberField(
            'compaction_target_pct',
            t('policy.compactionTarget'),
            {
              min: 10,
              max: PIPELINE_CAPS.compaction_target_pct,
              hint: t('policy.compactionTargetHint'),
            }
          )}
          {toggleField(
            'compaction_on_cold_cache',
            t('policy.compactOnColdCache'),
            t('policy.compactOnColdCacheHint')
          )}
        </div>
      </Collapsible>

      {/* ── Ejecución (Sprint 280; F2) ─────────────────────────────── */}
      <Collapsible
        testId="pipeline-section-execution"
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
        trigger={
          <span className="flex items-center gap-2">
            <Play className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span role="heading" aria-level={2} className="text-lg font-semibold">{t('policy.execution')}</span>
          </span>
        }
        contentClassName="space-y-4 pt-4"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.executionHint')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {selectField(
            'output_effort',
            t('policy.outputEffort'),
            OUTPUT_EFFORTS,
            L(OUTPUT_EFFORT_LABELS)
          )}
          {toggleField(
            'output_effort_dynamic',
            t('policy.dynamicOutputEffort'),
            t('policy.dynamicOutputEffortHint')
          )}
          {selectField(
            'async_service_tier',
            t('policy.asyncServiceTier'),
            ASYNC_SERVICE_TIERS,
            L(ASYNC_SERVICE_TIER_LABELS)
          )}
          {selectField(
            'async_tier_on_unavailable',
            t('policy.asyncTierOnUnavailable'),
            ASYNC_TIER_ON_UNAVAILABLE_VALUES,
            L(ASYNC_TIER_UNAVAILABLE_LABELS)
          )}
        </div>
      </Collapsible>

      {/* ── Presupuesto (Sprint 280; F3; solo tenant) ───────────────── */}
      {effectiveScope === 'tenant' && (
        <Collapsible
          testId="pipeline-section-budget"
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
          trigger={
            <span className="flex items-center gap-2">
              <Wallet className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <span role="heading" aria-level={2} className="text-lg font-semibold">{t('policy.budget')}</span>
            </span>
          }
          contentClassName="space-y-4 pt-4"
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('policy.budgetHint')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectField(
              'budget_aware_profile',
              t('policy.budgetAwareProfile'),
              BUDGET_AWARE_PROFILES,
              L(BUDGET_PROFILE_LABELS)
            )}
            {numberField(
              'budget_aware_threshold_pct',
              t('policy.budgetAwareThreshold'),
              {
                min: 50,
                max: PIPELINE_CAPS.budget_aware_threshold_pct,
                hint: t('policy.budgetAwareThresholdHint'),
              }
            )}
          </div>
        </Collapsible>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hive/agent settings adapter (bloques anidados ↔ campos planos)
// ---------------------------------------------------------------------------

/** Editor de overrides para los formularios de hive/agente existentes. */
export function PipelinePolicyOverrides({
  settings,
  onChange,
  responsibleLock,
}: {
  settings?: SettingsDict | null;
  onChange: (next: SettingsDict) => void;
  responsibleLock?: ResponsibleLock;
}) {
  const flat = settingsToPolicyFields(settings);
  return (
    <PipelinePolicySections
      mode="inherit"
      scope="entity"
      value={flat}
      onChange={(patch) =>
        onChange(policyFieldsToSettings({ ...flat, ...patch }, settings ?? {}))
      }
      responsibleLock={responsibleLock}
    />
  );
}
